import { agentApi } from "./apiClient";
import { getApiErrorMessage, getErrorMessage } from "./apiMessages";
import { AGENT_CUSTOMERS_PATH } from "./agentConfig";

export const EMPTY_CUSTOMER_FORM = {
  cust_name: "",
  cust_last_name: "",
  cust_email: "",
  cust_mobile: "",
  cust_pan: "",
  pan_card: null,
  pan_card_file_name: "",
  has_pan_card: false,
};

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;
const MOBILE_PATTERN = /^[0-9+\-\s]{8,15}$/;
const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const PAN_CARD_MAX_BYTES = 10 * 1024 * 1024;

export function formFromCustomer(customer = {}) {
  return {
    ...EMPTY_CUSTOMER_FORM,
    cust_name: customer.cust_name || "",
    cust_last_name: customer.cust_last_name || "",
    cust_email: customer.cust_email || "",
    cust_mobile: customer.cust_mobile || "",
    cust_pan: String(customer.cust_pan || "").trim().toUpperCase(),
    pan_card: null,
    pan_card_file_name: customer.cust_pan_card_name || "",
    has_pan_card: Boolean(customer.has_pan_card),
  };
}

function validatePanCardFile(file) {
  if (!file) return "";
  const name = String(file.name || "");
  const mime = String(file.type || "");
  const allowedType = /pdf|jpeg|jpg|png/i.test(mime) || /\.(pdf|jpe?g|png)$/i.test(name);
  if (!allowedType) return "Upload a PDF, JPG, or PNG PAN card photo.";
  if (Number(file.size || 0) > PAN_CARD_MAX_BYTES) return "PAN card file must be 10 MB or smaller.";
  return "";
}

export function parseCustomers(payload) {
  const data = payload?.data || payload || {};
  if (Array.isArray(data.customers)) return data.customers;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

export function customerIdOf(customer) {
  const value = customer?.cust_id ?? customer?.id ?? customer?.customerId;
  return value == null || value === "" ? "" : String(value);
}

export function customerOptionLabel(customer) {
  const name = `${customer?.cust_name || ""} ${customer?.cust_last_name || ""}`.trim() || "Customer";
  const mobile = String(customer?.cust_mobile || "").trim();
  return mobile ? `${name} - ${mobile}` : name;
}

export function toCustomerSelectOptions(customers = []) {
  return customers.map((customer) => ({
    value: customerIdOf(customer),
    label: customerOptionLabel(customer),
  })).filter((option) => option.value);
}

export function validateCustomerForm(form = EMPTY_CUSTOMER_FORM) {
  const errors = {};
  const firstName = String(form.cust_name || "").trim();
  const lastName = String(form.cust_last_name || "").trim();
  const email = String(form.cust_email || "").trim();
  const mobile = String(form.cust_mobile || "").trim();
  const pan = String(form.cust_pan || "").trim().toUpperCase();

  if (!firstName) errors.cust_name = "First name is required.";
  if (!lastName) errors.cust_last_name = "Last name is required.";
  if (!email) errors.cust_email = "Email is required.";
  else if (!EMAIL_PATTERN.test(email)) errors.cust_email = "Enter a valid email address.";
  if (!mobile) errors.cust_mobile = "Mobile is required.";
  else if (!MOBILE_PATTERN.test(mobile)) errors.cust_mobile = "Enter a valid mobile number.";
  if (pan && !PAN_PATTERN.test(pan)) errors.cust_pan = "Enter a valid PAN number (for example ABCDE1234F).";

  const panFileError = validatePanCardFile(form.pan_card);
  if (panFileError) errors.pan_card = panFileError;

  return errors;
}

export function customerPayloadFromForm(form = EMPTY_CUSTOMER_FORM) {
  return {
    cust_name: String(form.cust_name || "").trim(),
    cust_last_name: String(form.cust_last_name || "").trim(),
    cust_email: String(form.cust_email || "").trim(),
    cust_mobile: String(form.cust_mobile || "").trim(),
    cust_pan: String(form.cust_pan || "").trim().toUpperCase(),
  };
}

export function customerRequestBodyFromForm(form = EMPTY_CUSTOMER_FORM) {
  const payload = customerPayloadFromForm(form);
  if (!(form.pan_card instanceof File)) {
    return payload;
  }
  const body = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    body.append(key, value == null ? "" : String(value));
  });
  body.append("pan_card", form.pan_card);
  return body;
}

export function customerFromCreateResponse(payload, form = EMPTY_CUSTOMER_FORM) {
  const data = payload?.data || payload || {};
  const customer = data.customer && typeof data.customer === "object" ? data.customer : {};
  const fallback = customerPayloadFromForm(form);
  const customerId = customer.cust_id ?? data.customerId ?? data.cust_id ?? customer.id;

  return {
    ...fallback,
    ...customer,
    cust_id: customerId,
    cust_name: customer.cust_name || fallback.cust_name,
    cust_last_name: customer.cust_last_name || fallback.cust_last_name,
    cust_email: customer.cust_email || fallback.cust_email,
    cust_mobile: customer.cust_mobile || fallback.cust_mobile,
    cust_pan: customer.cust_pan || fallback.cust_pan || "",
    cust_pan_card_name: customer.cust_pan_card_name || "",
    has_pan_card: Boolean(customer.has_pan_card),
    cust_status: customer.cust_status || "active",
  };
}

export function upsertCustomer(customers = [], customer) {
  const id = customerIdOf(customer);
  if (!id) return customers;
  const next = Array.isArray(customers) ? [...customers] : [];
  const index = next.findIndex((item) => customerIdOf(item) === id);
  if (index >= 0) {
    next[index] = { ...next[index], ...customer };
    return next;
  }
  return [customer, ...next];
}

export function matchCreatedCustomer(customers = [], form = EMPTY_CUSTOMER_FORM) {
  const payload = customerPayloadFromForm(form);
  return customers.find((customer) => {
    const email = String(customer?.cust_email || "").trim().toLowerCase();
    const mobile = String(customer?.cust_mobile || "").trim();
    const name = String(customer?.cust_name || "").trim();
    if (payload.cust_email && email === payload.cust_email.toLowerCase()) return true;
    return mobile === payload.cust_mobile && name === payload.cust_name;
  }) || null;
}

export function mapCustomerServerError(error) {
  const raw = typeof error === "string" ? error : String(error?.rawMessage || error?.payload?.message || error?.message || "");
  const formError = getApiErrorMessage(error, "SAVE_CUSTOMER");
  const fieldErrors = {};
  const duplicate = /exist|duplicate|already/i.test(raw);
  if (duplicate && /email/i.test(raw)) fieldErrors.cust_email = getErrorMessage("CUSTOMER_EMAIL_EXISTS");
  if (duplicate && /mobile/i.test(raw)) fieldErrors.cust_mobile = getErrorMessage("CUSTOMER_MOBILE_EXISTS");
  if (/pan card file/i.test(raw) || /only pdf, jpg, and png pan/i.test(raw)) {
    fieldErrors.pan_card = getErrorMessage("CUSTOMER_PAN_FILE");
  } else if (/valid pan/i.test(raw)) {
    fieldErrors.cust_pan = getErrorMessage("CUSTOMER_PAN_INVALID");
  }
  return { formError, fieldErrors };
}

export function customerRequestErrorMessage(error, fallback = "SAVE_CUSTOMER") {
  return getApiErrorMessage(error, fallback);
}

export async function fetchAgentCustomers({ search = "", limit = 100, page = 1 } = {}) {
  const payload = await agentApi.get(AGENT_CUSTOMERS_PATH, {
    params: { limit, page, search },
  });
  return parseCustomers(payload);
}

export async function createAgentCustomer(form) {
  const payload = await agentApi.post(AGENT_CUSTOMERS_PATH, {
    body: customerRequestBodyFromForm(form),
  });
  return { payload, customer: customerFromCreateResponse(payload, form) };
}

export async function updateAgentCustomer(customerId, form) {
  const payload = await agentApi.put(`${AGENT_CUSTOMERS_PATH}/${customerId}`, {
    body: customerRequestBodyFromForm(form),
  });
  return payload;
}
